<template>
  <div class="cart-line">
    <!-- Slot header: game thumbnail + name + date · time + remove. Mirrors
         the drawer's own header layout so cart lines feel like miniature
         booking cards rather than a styled list. -->
    <div class="cart-line__header">
      <img
        v-if="slotEntry.game_image"
        v-twin-src="slotEntry.game_image"
        :alt="slotEntry.game_name"
        class="cart-line__thumb"
      />
      <div class="cart-line__title-block">
        <div class="cart-line__name text-white">{{ slotEntry.game_name }}</div>
        <div v-if="formattedMeta" class="cart-line__meta">{{ formattedMeta }}</div>
      </div>
      <button
        type="button"
        class="cart-line__remove"
        :aria-label="`Remove ${slotEntry.game_name} from cart`"
        @click="$emit('remove', slotEntry.id)"
      >
        <i class="tim-icons icon-simple-remove"></i>
      </button>
    </div>

    <div v-if="loading" class="cart-line__loading">Loading pricing...</div>

    <template v-else>
      <!-- Upgrade-to-private toggle (mirrors the single-slot Tickets
           section). Only available when the slot is public-bookable AND
           the game allows the upgrade. Locks the line's subtotal to the
           reservation upgrade fee while engaged. -->
      <div v-if="canUpgradeToPrivate" class="upgrade-toggle">
        <el-switch
          active-color="#e14eca"
          v-model="upgradeToPrivate"
          @change="onChange()"
        />
        <label class="upgrade-toggle__label text-white">
          Upgrade to private reservation
          <el-tooltip placement="top" effect="light" :open-delay="300">
            <div slot="content">
              Book the entire time slot exclusively for this customer.
              <template v-if="upgradePricingMode === 'tickets_plus_fee'">Charged as per ticket pricing plus a {{ getAmount(upgradeFee) }} private booking fee.</template>
              <template v-else>Buyout fee of {{ getAmount(upgradeFee) }}.</template>
            </div>
            <i class="tim-icons el-icon-info upgrade-toggle__info"></i>
          </el-tooltip>
        </label>
      </div>

      <div
        v-for="(c, i) in pricingCategories"
        :key="'cl-cat-' + i"
        class="pricing-row"
      >
        <div class="pricing-row__info">
          <div class="pricing-row__name-line">
            <span class="pricing-row__name text-white">{{ c.category }}</span>
            <template v-if="!c.editing">
              <el-tooltip content="Override price" placement="top" :open-delay="300">
                <i class="tim-icons icon-pencil pricing-row__edit-icon" @click="startEditPrice(c)"></i>
              </el-tooltip>
            </template>
          </div>
          <template v-if="!c.editing">
            <span class="pricing-row__rate">{{ getRate(c) }}</span>
          </template>
          <template v-else>
            <div class="pricing-row__inline-edit">
              <base-input class="mb-0 pricing-row__price-input">
                <el-input-number
                  v-model="c.price"
                  :min="0"
                  :precision="2"
                  :step="0.01"
                  :controls="false"
                  size="mini"
                  @change="onChange()"
                />
              </base-input>
              <el-tooltip content="Save" placement="top" :open-delay="300">
                <i class="tim-icons icon-check-2 pricing-row__save-icon" @click="saveEditPrice(c)"></i>
              </el-tooltip>
              <el-tooltip content="Restore original price" placement="top" :open-delay="300">
                <i class="tim-icons icon-refresh-02 pricing-row__restore-icon" @click="restoreOriginalPrice(c)"></i>
              </el-tooltip>
              <el-tooltip content="Cancel" placement="top" :open-delay="300">
                <i class="tim-icons icon-simple-remove pricing-row__cancel-icon" @click="cancelEditPrice(c)"></i>
              </el-tooltip>
            </div>
          </template>
        </div>
        <div class="pricing-row__qty">
          <base-input class="mb-0">
            <el-input-number
              v-model="c.number_of_players"
              :min="0"
              size="mini"
              @change="onChange()"
            />
          </base-input>
        </div>
        <div v-if="!upgradeToPrivate || upgradePricingMode === 'tickets_plus_fee'" class="pricing-row__total text-white">
          {{ getAmount(getLineTotal(c)) }}
        </div>
      </div>

      <!-- Per-slot subtotal — the cart's tax/fee/promo math at the parent
           sums these into the aggregate subtotal. -->
      <div class="cart-line__subtotal">
        <span class="cart-line__subtotal-label text-white">Subtotal</span>
        <span class="cart-line__subtotal-amount text-white">{{ getAmount(subtotal) }}</span>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { Auth } from 'aws-amplify';
import { API_LOCATION } from '@/api-config';
import { calculateSubtotal, roundCents, findPriceForPlayerCount, applyPrivateUpgrade, filterPricingByDuration } from '@/util/pricing';
import { formatCurrency } from '@/util/currencyFormat';
import moment from 'moment';
import _ from 'lodash';

export default {
  name: 'CartLine',

  props: {
    // One entry from multiSelectState.cartSlots: { id, game_id, game_name,
    // booking_date, end_date, start_time, end_time, is_public_booking }.
    // Display fields are captured from the calendar card at click time so
    // we can render the header without a fetch; pricing is fetched on
    // mount via companies-get-pricing-for-slot.
    slotEntry: { type: Object, required: true },
    companyId: { type: [Number, String], required: true },
    companyGroupId: { type: [Number, String], required: true },
    dateFormat: { type: String, default: 'MM-DD-YYYY' },
    timeFormat: { type: String, default: '12h' },
    currency: { type: String, default: 'USD' },
    currencySymbol: { type: String, default: '$' },
    currencyFormat: { type: Number, default: 0 },
    numberFormat: { type: Number, default: 0 }
  },

  data() {
    return {
      loading: true,
      pricing: [],
      // Lambda's settings object: pricing_type_id, cumulative_category_pricing,
      // allow_reservation_type_upgrade, reservation_upgrade_fee, etc.
      settings: {},
      pricingCategories: [],
      upgradeToPrivate: false
    };
  },

  computed: {
    formattedMeta() {
      const parts = [];
      if (this.slotEntry.booking_date) {
        const dateStr = String(this.slotEntry.booking_date).substring(0, 10);
        parts.push(moment(dateStr, 'YYYY-MM-DD').format(this.dateFormat));
      }
      if (this.slotEntry.start_time) {
        const time = String(this.slotEntry.start_time).substring(0, 5);
        const is24 = this.timeFormat === 'HH:mm';
        parts.push(is24 ? time : moment(time, 'HH:mm').format('h:mm A'));
      }
      return parts.join(' · ');
    },

    canUpgradeToPrivate() {
      return !!(this.settings && this.settings.is_public_booking && this.settings.allow_reservation_type_upgrade);
    },

    upgradeFee() {
      return parseFloat(this.settings && this.settings.reservation_upgrade_fee) || 0;
    },

    upgradePricingMode() {
      return (this.settings && this.settings.reservation_upgrade_pricing_mode) || 'buyout';
    },

    numberOfPlayers() {
      return this.pricingCategories.reduce((sum, c) => sum + (Number(c.number_of_players) || 0), 0);
    },

    pricingTypeId() {
      return Number(this.settings && this.settings.pricing_type_id) || 1;
    },

    cumulativePricing() {
      return !!(this.settings && this.settings.cumulative_category_pricing);
    },

    // Per-line subtotal. Upgrade-to-private routes through applyPrivateUpgrade:
    // 'buyout' replaces the per-ticket total with the flat fee; 'tickets_plus_fee'
    // adds the nominal fee on top of normal per-ticket pricing.
    subtotal() {
      const result = calculateSubtotal({
        pricing: this.pricing,
        categories: this.pricingCategories,
        cumulativePricing: this.cumulativePricing,
        pricingTypeId: this.pricingTypeId
      });
      const base = roundCents(parseFloat(result.subtotal) || 0);
      if (this.upgradeToPrivate) {
        return roundCents(applyPrivateUpgrade({ baseSubtotal: base, mode: this.upgradePricingMode, fee: this.upgradeFee }));
      }
      return base;
    }
  },

  watch: {
    // Re-emit whenever the per-line subtotal changes for any reason
    // (stepper, override edit, upgrade toggle, pricing fetch landing).
    subtotal() { this.emitUpdate(); },
    pricingCategories: { deep: true, handler() { this.emitUpdate(); } }
  },

  async mounted() {
    await this.loadPricing();
  },

  methods: {
    async getAuthToken() {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    },

    async loadPricing() {
      try {
        const token = await this.getAuthToken();
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/bookings/${this.slotEntry.id}/pricing`;
        const response = await axios.get(url, { headers: { Authorization: token } });
        const { pricing, settings } = response.data || {};
        this.pricing = Array.isArray(pricing) ? pricing : [];
        this.settings = settings || {};
        //Multiple durations: cart mode has no picker, so a line prices the slot's booked duration or the default
        this.pricingCategories = this.buildPricingCategories(filterPricingByDuration(this.pricing, this.settings.selected_duration_id ?? null));
        this.emitUpdate();
      } catch (err) {
        console.error(`Failed to fetch pricing for slot ${this.slotEntry.id}:`, err);
        this.pricing = [];
        this.pricingCategories = [];
      } finally {
        this.loading = false;
      }
    },

    // Reduce the lambda's tier rows (multiple per category) into one
    // entry per category, pre-filled at the category's default count.
    // Mirrors CreateBookingView's loadPricingCategories shape so price
    // override editing works the same way.
    buildPricingCategories(pricing) {
      if (!Array.isArray(pricing) || pricing.length === 0) return [];
      const seen = new Map();
      for (const row of pricing) {
        if (row && row.category_id != null && !seen.has(row.category_id)) {
          seen.set(row.category_id, {
            category_id: row.category_id,
            category: row.category,
            price: row.price,
            number_of_players: Number(row.default_participants) || 0,
            category_pricing: pricing.filter(p => p.category_id === row.category_id),
            pricing: pricing.filter(p => p.category_id === row.category_id)
          });
        }
      }
      return Array.from(seen.values()).sort((a, b) => a.category_id - b.category_id);
    },

    onChange() {
      // Vue watcher on pricingCategories (deep) fires after the next tick,
      // but el-input-number `change` is synchronous — this forces an
      // immediate emit so the parent's aggregate subtotal updates in the
      // same frame as the stepper click.
      this.$nextTick(() => this.emitUpdate());
    },

    emitUpdate() {
      this.$emit('update', {
        slotId: this.slotEntry.id,
        gameId: this.slotEntry.game_id,
        isPublicBooking: this.slotEntry.is_public_booking,
        subtotal: this.subtotal,
        numberOfPlayers: this.numberOfPlayers,
        upgradeFee: this.upgradeToPrivate ? this.upgradeFee : 0,
        upgradeToPrivate: this.upgradeToPrivate,
        upgradePricingMode: this.upgradePricingMode,
        tickets: this.pricingCategories.map(c => ({
          category_id: c.category_id,
          category: c.category,
          number_of_players: Number(c.number_of_players) || 0,
          price: parseFloat(c.price) || 0
        }))
      });
    },

    getAmount(value) {
      return formatCurrency(value, {
        currency: this.currency,
        currencySymbol: this.currencySymbol,
        currencyFormat: this.currencyFormat,
        numberFormat: this.numberFormat
      });
    },

    // Per-category rate label. For per-person pricing this is "$X each";
    // flat-rate shows "$X flat rate for N-M <category>". Mirrors how the
    // single-slot Tickets section formats the rate line.
    getRate(c) {
      if (!c || !c.category_pricing || c.category_pricing.length === 0) {
        return this.getAmount(c.price || 0);
      }
      const players = Number(c.number_of_players) || 0;
      const totalPlayersAcrossLine = this.numberOfPlayers;
      const lookupCount = this.cumulativePricing ? totalPlayersAcrossLine : players;
      const ratePerPerson = findPriceForPlayerCount(c.category_pricing, lookupCount) || c.price || 0;
      if (this.pricingTypeId === 2) {
        // Flat-rate: find the tier that covers lookupCount to label the range
        const tier = _.find(c.category_pricing, t => lookupCount >= t.min_players && lookupCount <= t.max_players)
          || _.minBy(c.category_pricing, 'min_players');
        if (tier) {
          return `${this.getAmount(ratePerPerson)} flat rate for ${tier.min_players}-${tier.max_players} ${c.category}`;
        }
        return `${this.getAmount(ratePerPerson)} flat rate`;
      }
      return `${this.getAmount(ratePerPerson)} each`;
    },

    getLineTotal(c) {
      const players = Number(c.number_of_players) || 0;
      if (players <= 0) return 0;
      // Has user override?
      if (Object.prototype.hasOwnProperty.call(c, 'original_price')) {
        return roundCents(c.price * players);
      }
      const lookupCount = this.cumulativePricing ? this.numberOfPlayers : players;
      const tierPrice = findPriceForPlayerCount(c.category_pricing || [], lookupCount) || c.price || 0;
      if (this.pricingTypeId === 2) {
        return roundCents(tierPrice);
      }
      return roundCents(tierPrice * players);
    },

    // Price-override editing (inline pencil → input → save / cancel / restore).
    // Stashes the tier price as original_price so the parent's pricing
    // math knows to bypass tier-lookup for that category.
    startEditPrice(c) {
      this.$set(c, 'editing', true);
      // Capture current tier price as the "original" so restore works
      if (!Object.prototype.hasOwnProperty.call(c, 'original_price')) {
        const currentTier = findPriceForPlayerCount(c.category_pricing || [], this.numberOfPlayers) || c.price;
        this.$set(c, 'original_price', currentTier);
        this.$set(c, 'editing_initial', c.price);
      } else {
        this.$set(c, 'editing_initial', c.price);
      }
    },

    saveEditPrice(c) {
      this.$set(c, 'editing', false);
      this.$delete(c, 'editing_initial');
      this.emitUpdate();
    },

    cancelEditPrice(c) {
      if (Object.prototype.hasOwnProperty.call(c, 'editing_initial')) {
        c.price = c.editing_initial;
        this.$delete(c, 'editing_initial');
      }
      this.$set(c, 'editing', false);
    },

    restoreOriginalPrice(c) {
      if (Object.prototype.hasOwnProperty.call(c, 'original_price')) {
        c.price = c.original_price;
        this.$delete(c, 'original_price');
      }
      this.$set(c, 'editing', false);
      this.$delete(c, 'editing_initial');
      this.emitUpdate();
    }
  }
};
</script>

<style lang="scss" scoped>
.cart-line {
  padding: 12px 0;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.08);

  &:last-of-type {
    border-bottom: none;
  }
}

.cart-line__header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.cart-line__thumb {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.05);
}

.cart-line__title-block {
  min-width: 0;
  flex: 1;
}

.cart-line__name {
  font-size: 15px;
  font-weight: 700;
  line-height: 1.25;
}

.cart-line__meta {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.55);
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
}

.cart-line__remove {
  flex-shrink: 0;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: rgba(245, 54, 92, 0.12);
    border-color: rgba(245, 54, 92, 0.6);
    color: #f5365c;
  }

  i { font-size: 10px; }
}

.cart-line__loading {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
  padding: 8px 0;
}

// Mirrors CreateBookingView's .upgrade-toggle so the cart-mode look
// matches the single-slot Tickets section exactly. Scoped CSS isolates
// each component, so the styles can't be shared via inheritance — copied
// rather than extracted because the rules are short and self-contained.
.upgrade-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  margin-bottom: 12px;
  background: rgba(225, 78, 202, 0.08);
  border-left: 3px solid #e14eca;
  border-radius: 6px;
}

.upgrade-toggle__label {
  font-size: 13px;
  margin-bottom: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.upgrade-toggle__info {
  color: #1d8cf8;
  cursor: help;
  font-size: 14px;
}

// Mirrors CreateBookingView's .pricing-row layout: name+rate on the
// left flex-growing, qty stepper at a fixed width in the middle, line
// total on the right.
.pricing-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);

  &:last-of-type { border-bottom: none; }
}

.pricing-row__info {
  flex: 1;
  min-width: 0;
}

.pricing-row__name-line {
  display: flex;
  align-items: center;
  gap: 6px;
}

.pricing-row__name {
  font-size: 13px;
  font-weight: 500;
}

.pricing-row__rate {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.8);
  display: block;
  margin-top: 2px;
}

.pricing-row__edit-icon {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  &:hover { color: #e14eca; }
}

.pricing-row__inline-edit {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
}

.pricing-row__price-input { width: 90px; }

.pricing-row__save-icon {
  color: #2dce89;
  cursor: pointer;
  font-size: 12px;
  &:hover { color: #24a46d; }
}

.pricing-row__restore-icon {
  color: #1d8cf8;
  cursor: pointer;
  font-size: 12px;
  &:hover { color: #1171d6; }
}

.pricing-row__cancel-icon {
  color: #f5365c;
  cursor: pointer;
  font-size: 12px;
  &:hover { color: #d42a4a; }
}

.pricing-row__qty { width: 110px; }

.pricing-row__total {
  width: 80px;
  text-align: right;
  font-weight: 500;
  font-size: 13px;
  padding-top: 22px;
}

.cart-line__subtotal {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px dashed rgba(255, 255, 255, 0.10);
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.cart-line__subtotal-label {
  color: rgba(255, 255, 255, 0.75);
  font-weight: 500;
}

// Light-mode parity
.white-content {
  .cart-line { border-bottom-color: rgba(52, 70, 117, 0.10); }
  .cart-line__meta,
  .cart-line__loading { color: rgba(52, 70, 117, 0.6); }
  .cart-line__subtotal { border-top-color: rgba(52, 70, 117, 0.15); }
  .cart-line__subtotal-label { color: rgba(52, 70, 117, 0.75); }
  .cart-line__remove {
    border-color: rgba(52, 70, 117, 0.20);
    color: rgba(52, 70, 117, 0.55);
  }
}
</style>
